// Considered: UnifiedFilterEditor / FilterGroupCardMolecule (the spec's Reuse Audit names them
// as the mandated builder) — rejected for the rule condition tree, and the Audit amended to
// record it. Those edit `UnifiedFilters`: lanes of property/perspective/relationship/arc
// filters that select SPHERES. A business rule's condition is a `BusinessRuleCondition` — an
// and/or tree of `BusinessRuleExpression`s over ONE sphere's own properties, whose defining
// capability is `compareToProperty`: comparing two properties of the same sphere against each
// other. `UnifiedFilters` has no representation for that, so routing a rule through the filter
// editor would silently drop every cross-property rule — which is most of what this block is
// for. The filter editor stays the builder for target/applicability scope (the relationship
// sheet composes it); this molecule is the builder for rule conditions. It reuses the filter
// editor's shape (a lead + all/any + rows + an add row) rather than its state model.
import React, { useState } from 'react';
import { useTranslation } from 'localization';
import type {
  BusinessRuleCondition,
  BusinessRuleExpression,
  BusinessRuleOperator,
} from 'types/businessRules';
import type { SchemaProperty } from 'types/schema';
import {
  allowedValues,
  isComparative,
  operatorArity,
  propertyEligibility,
} from '../../../utils/ruleProblems';
import InputAtom from '../../atoms/Input/InputAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import './RuleConditionBuilderMolecule.scss';

const KEY = 'studio.typeEditor.businessRules';

/** Contract order (`BR_OPERATORS`, type-rule.jsx) — not the type union's order. */
export const RULE_OPERATORS: BusinessRuleOperator[] = [
  'equals',
  'not_equals',
  'exists',
  'greater_than',
  'greater_than_or_equal',
  'less_than',
  'less_than_or_equal',
  'contains',
  'in',
  'not_in',
  'is_true',
  'is_false',
];

/**
 * The contract ids for one expression row. The tree is mounted four times over — the gate's
 * *If* and the requirement's *Then it must*, each with a top-level and an inside-a-group
 * reading — and each mounting names its own ids.
 */
export interface RuleExpressionParity {
  joiner: string;
  property: string;
  caret: string;
  operator: string;
  valueSummary: string;
  valueCaret: string;
  remove: string;
  search: string;
  searchClose: string;
  pickerItem: string;
  pickerItemLabel: string;
  pickerItemNote: string;
  compareValue: string;
  compareProperty: string;
  comparePropertySelect: string;
  optionPill: string;
  listInput: string;
  valueInput: string;
}

export interface RuleConditionBuilderParity {
  lead: string;
  all: string;
  any: string;
  ofTheFollowing: string;
  spacer: string;
  clear: string;
  addCondition: string;
  addGroup: string;
  group: {
    joiner: string;
    label: string;
    all: string;
    any: string;
    spacer: string;
    remove: string;
    add: string;
  };
  groupExpression: RuleExpressionParity;
  expression: RuleExpressionParity;
}

/**
 * Seeds the interaction state a capture has to BE rather than become: an open property
 * picker, an open value editor. Keyed by the row's path — `"2"` for the third top-level row,
 * `"1.0"` for the first row of the second group.
 */
export interface RuleConditionBuilderInitialState {
  pickingPath?: string;
  openValuePath?: string;
  query?: string;
}

export interface RuleConditionBuilderMoleculeProps {
  value?: BusinessRuleCondition;
  onChange: (next: BusinessRuleCondition) => void;
  properties: SchemaProperty[];
  /** The sentence the head opens with — *This is true* for the gate, *Must satisfy* for the requirement. */
  lead: string;
  emptyNote: string;
  readOnly?: boolean;
  parity: RuleConditionBuilderParity;
  initialState?: RuleConditionBuilderInitialState;
  testId?: string;
}

const isCondition = (
  entry: BusinessRuleExpression | BusinessRuleCondition
): entry is BusinessRuleCondition => Array.isArray((entry as BusinessRuleCondition).expressions);

const labelOf = (properties: SchemaProperty[], name?: string) =>
  properties.find((p) => p.name === name)?.label || name || '';

type Translate = (key: string, params?: Record<string, unknown>) => string;

/** The picker's groups: what this type owns, what an aspect holds, what a parent declared. */
function pickerGroups(properties: SchemaProperty[], query: string, t: Translate) {
  const q = query.trim().toLowerCase();
  const groups: { name: string; items: SchemaProperty[] }[] = [];
  properties
    .filter((p) => !q || `${p.label ?? ''} ${p.name}`.toLowerCase().includes(q))
    .forEach((p) => {
      const heldBy = (p as { from_aspect?: string }).from_aspect;
      const inheritedFrom = (p as { inherited_from_name?: string }).inherited_from_name;
      const name = heldBy
        ? t(`${KEY}.pickerGroup.heldBy`, { name: heldBy })
        : inheritedFrom
          ? t(`${KEY}.pickerGroup.from`, { name: inheritedFrom })
          : t(`${KEY}.pickerGroup.own`);
      const hit = groups.find((g) => g.name === name);
      if (hit) hit.items.push(p);
      else groups.push({ name, items: [p] });
    });
  return groups;
}

const RuleConditionBuilderMolecule: React.FC<RuleConditionBuilderMoleculeProps> = ({
  value,
  onChange,
  properties,
  lead,
  emptyNote,
  readOnly = false,
  parity,
  initialState,
  testId = 'rule-condition-builder',
}) => {
  const { t } = useTranslation();
  const tree: BusinessRuleCondition = value ?? { operator: 'and', expressions: [] };
  const [picking, setPicking] = useState<string | undefined>(initialState?.pickingPath);
  const [openValue, setOpenValue] = useState<string | undefined>(initialState?.openValuePath);
  const [query, setQuery] = useState(initialState?.query ?? '');

  const set = (patch: Partial<BusinessRuleCondition>) => onChange({ ...tree, ...patch });
  const blank = (): BusinessRuleExpression => ({
    property: properties[0]?.name ?? '',
    operator: 'exists',
  });
  const setAt = (
    index: number,
    patch: Partial<BusinessRuleExpression> | Partial<BusinessRuleCondition>
  ) =>
    set({
      expressions: tree.expressions.map((entry, i) =>
        i === index ? ({ ...entry, ...patch } as BusinessRuleExpression | BusinessRuleCondition) : entry
      ),
    });
  const removeAt = (index: number) =>
    set({ expressions: tree.expressions.filter((_, i) => i !== index) });

  const joinerWord = t(`${KEY}.joiner.${tree.operator}`);

  /** One expression row — the same shape at both nesting depths, with its own ids. */
  const expressionRow = (
    expression: BusinessRuleExpression,
    path: string,
    index: number,
    joiner: string,
    ids: RuleExpressionParity,
    change: (patch: Partial<BusinessRuleExpression>) => void,
    remove: () => void
  ) => {
    const arity = operatorArity(expression.operator);
    const property = properties.find((p) => p.name === expression.property);
    const eligible = propertyEligibility(property);
    const universe = allowedValues(property);
    const usingProperty = !!expression.compareToProperty;
    const isPicking = picking === path;
    const isOpen = openValue === path;
    const setValue = (next: unknown) => change({ value: next, compareToProperty: undefined });
    const listValue: unknown[] = Array.isArray(expression.value) ? expression.value : [];
    const summary = usingProperty
      ? labelOf(properties, expression.compareToProperty)
      : arity === 'list'
        ? listValue.length
          ? listValue.join(t(`${KEY}.valueSeparator`))
          : t(`${KEY}.chooseValue`)
        : expression.value === '' || expression.value === undefined
          ? t(`${KEY}.chooseValue`)
          : String(expression.value);
    const groups = pickerGroups(properties, query, t);

    return (
      <div className="rule-condition__row" key={path} data-container="each(expressions)" data-testid={`${testId}-row-${path}`}>
        <span className="rule-condition__joiner" data-datum={ids.joiner} data-kind="text">
          {index === 0 ? '' : joiner}
        </span>
        <div className="rule-condition__clause">
          <div className="rule-condition__clause-main">
            <button
              type="button"
              className="rule-condition__field"
              disabled={readOnly}
              data-bad={eligible.ok ? undefined : '1'}
              data-datum={ids.property}
              data-kind="button"
              data-testid={`${testId}-property-${path}`}
              onClick={() => setPicking(isPicking ? undefined : path)}
            >
              {expression.property
                ? labelOf(properties, expression.property)
                : t(`${KEY}.chooseAProperty`)}
              <span
                className="rule-condition__caret"
                data-datum={ids.caret}
                data-kind="text"
                aria-hidden="true"
              >
                ▾
              </span>
            </button>
            <DropdownAtom
              className="rule-condition__operator"
              dropdownSize="small"
              value={expression.operator}
              disabled={readOnly}
              aria-label={t(`${KEY}.operatorAria`)}
              data-datum={ids.operator}
              data-kind="select"
              data-testid={`${testId}-operator-${path}`}
              optionLabel="label"
              optionValue="value"
              options={RULE_OPERATORS.map((operator) => ({
                label: t(`${KEY}.operator.${operator}`),
                value: operator,
              }))}
              onChange={(event) => {
                const next = event.value as BusinessRuleOperator;
                const nextArity = operatorArity(next);
                change({
                  operator: next,
                  value: nextArity === 'none' ? undefined : nextArity === 'list' ? [] : '',
                  compareToProperty:
                    nextArity === 'either' ? expression.compareToProperty : undefined,
                });
              }}
            />
            {arity !== 'none' && (
              <>
                <button
                  type="button"
                  className="rule-condition__summary"
                  disabled={readOnly}
                  data-unset={
                    (usingProperty ? false : arity === 'list' ? !listValue.length : !expression.value)
                      ? '1'
                      : undefined
                  }
                  data-datum={ids.valueSummary}
                  data-kind="button"
                  data-testid={`${testId}-value-${path}`}
                  onClick={() => setOpenValue(isOpen ? undefined : path)}
                >
                  {summary}
                </button>
                <button
                  type="button"
                  className="rule-condition__value-caret"
                  disabled={readOnly}
                  aria-label={t(isOpen ? `${KEY}.closeValueEditor` : `${KEY}.openValueEditor`)}
                  data-datum={ids.valueCaret}
                  data-kind="button"
                  onClick={() => setOpenValue(isOpen ? undefined : path)}
                >
                  {isOpen ? '▴' : '▾'}
                </button>
              </>
            )}
            {!readOnly && (
              <button
                type="button"
                className="rule-condition__x"
                aria-label={t(`${KEY}.removeCondition`)}
                data-datum={ids.remove}
                data-kind="button"
                data-testid={`${testId}-remove-${path}`}
                onClick={remove}
              >
                ✕
              </button>
            )}
          </div>

          {!eligible.ok && expression.property && (
            <div className="rule-condition__scope" data-bad="1">
              {t(`${KEY}.eligibility.${eligible.whyKey}`)}
            </div>
          )}

          {isPicking && !readOnly && (
            <div className="rule-condition__picker" data-testid={`${testId}-picker-${path}`}>
              <div className="rule-condition__picker-search">
                <InputAtom
                  value={query}
                  aria-label={t(`${KEY}.searchProperties`)}
                  placeholder={t(`${KEY}.searchProperties`)}
                  data-datum={ids.search}
                  data-kind="input"
                  onChange={(event) => setQuery(event.target.value)}
                />
                <button
                  type="button"
                  aria-label={t(`${KEY}.closePicker`)}
                  data-datum={ids.searchClose}
                  data-kind="button"
                  onClick={() => setPicking(undefined)}
                >
                  ✕
                </button>
              </div>
              <div className="rule-condition__scope">{t(`${KEY}.pickerScope`)}</div>
              <div className="rule-condition__picker-list">
                {groups.map((group) => (
                  <div key={group.name} data-container="each(groups)">
                    <div className="rule-condition__picker-group">{group.name}</div>
                    {group.items.map((item) => {
                      const ok = propertyEligibility(item);
                      return (
                        <button
                          type="button"
                          key={item.name}
                          className="rule-condition__picker-item"
                          data-container="each(groups) > each(items)"
                          disabled={!ok.ok}
                          data-on={item.name === expression.property ? '1' : undefined}
                          data-datum={ids.pickerItem}
                          data-kind="button"
                          data-testid={`${testId}-picker-item-${item.name}`}
                          onClick={() => {
                            change({ property: item.name });
                            setPicking(undefined);
                            setQuery('');
                          }}
                        >
                          <span data-datum={ids.pickerItemLabel} data-kind="text">
                            {item.label || item.name}
                          </span>
                          <span
                            className="rule-condition__picker-note"
                            data-datum={ids.pickerItemNote}
                            data-kind="text"
                          >
                            {ok.ok
                              ? t(`${KEY}.pickerItemType`, {
                                  type: item.type,
                                  required: item.required ? t(`${KEY}.pickerItemRequired`) : '',
                                })
                              : t(`${KEY}.eligibility.${ok.whyKey}`)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
                {!groups.length && (
                  <div className="rule-condition__picker-empty">{t(`${KEY}.nothingMatches`)}</div>
                )}
              </div>
            </div>
          )}

          {isOpen && arity !== 'none' && (
            <div className="rule-condition__editor" data-testid={`${testId}-editor-${path}`}>
              {arity === 'either' && (
                <div className="rule-condition__ed">
                  <div className="rule-condition__ed-label">{t(`${KEY}.compareAgainst`)}</div>
                  <div className="rule-condition__seg">
                    <button
                      type="button"
                      data-on={!usingProperty ? '1' : undefined}
                      disabled={readOnly}
                      data-datum={ids.compareValue}
                      data-kind="button"
                      onClick={() => setValue('')}
                    >
                      {t(`${KEY}.aValue`)}
                    </button>
                    <button
                      type="button"
                      data-on={usingProperty ? '1' : undefined}
                      disabled={readOnly}
                      data-datum={ids.compareProperty}
                      data-kind="button"
                      onClick={() =>
                        change({
                          compareToProperty: properties.find((p) => p.name !== expression.property)
                            ?.name,
                          value: undefined,
                        })
                      }
                    >
                      {t(`${KEY}.anotherProperty`)}
                    </button>
                  </div>
                </div>
              )}
              {usingProperty ? (
                <div className="rule-condition__ed">
                  <div className="rule-condition__ed-label">{t(`${KEY}.propertyLabel`)}</div>
                  <DropdownAtom
                    className="rule-condition__select"
                    dropdownSize="small"
                    value={expression.compareToProperty}
                    disabled={readOnly}
                    aria-label={t(`${KEY}.propertyLabel`)}
                    data-datum={ids.comparePropertySelect}
                    data-kind="select"
                    data-testid={`${testId}-compare-property-${path}`}
                    optionLabel="label"
                    optionValue="value"
                    options={properties
                      .filter((p) => p.name !== expression.property && propertyEligibility(p).ok)
                      .map((p) => ({ label: p.label || p.name, value: p.name }))}
                    onChange={(event) => change({ compareToProperty: event.value as string })}
                  />
                  {/*
                    `type-rule.jsx:328` — a relational operator against another property
                    returns false, not "skip", when either side is not a number or a date.
                    The note states that where the choice is made, not after it fails.
                  */}
                  {isComparative(expression.operator) && (
                    <div className="rule-condition__picker-scope" data-kind="text">
                      {t(`${KEY}.bothSidesNote`)}
                    </div>
                  )}
                </div>
              ) : arity === 'list' ? (
                <div className="rule-condition__ed">
                  <div className="rule-condition__ed-label">
                    {t(universe ? `${KEY}.pickAnyThatApply` : `${KEY}.valuesCommaSeparated`)}
                  </div>
                  {universe ? (
                    <div className="rule-condition__chips">
                      {universe.map((option) => {
                        const on = listValue.includes(option);
                        return (
                          <button
                            type="button"
                            key={String(option)}
                            className="rule-condition__chip"
                            data-container="each(universe)"
                            data-on={on ? '1' : undefined}
                            disabled={readOnly}
                            data-datum={ids.optionPill}
                            data-kind="button"
                            onClick={() =>
                              setValue(
                                on ? listValue.filter((v) => v !== option) : [...listValue, option]
                              )
                            }
                          >
                            {String(option)}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <InputAtom
                      className="rule-condition__part"
                      value={listValue.join(t(`${KEY}.valueSeparator`))}
                      readOnly={readOnly}
                      aria-label={t(`${KEY}.valuesCommaSeparated`)}
                      data-datum={ids.listInput}
                      data-kind="input"
                      onChange={(event) =>
                        setValue(
                          event.target.value
                            .split(',')
                            .map((s) => s.trim())
                            .filter(Boolean)
                        )
                      }
                    />
                  )}
                </div>
              ) : (
                <div className="rule-condition__ed">
                  <div className="rule-condition__ed-label">{t(`${KEY}.valueLabel`)}</div>
                  <InputAtom
                    className="rule-condition__part"
                    value={expression.value === undefined ? '' : String(expression.value)}
                    readOnly={readOnly}
                    aria-label={t(`${KEY}.valueLabel`)}
                    placeholder={t(`${KEY}.aValuePlaceholder`)}
                    data-datum={ids.valueInput}
                    data-kind="input"
                    onChange={(event) => setValue(event.target.value)}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="rule-condition" data-testid={testId}>
      <div className="rule-condition__card">
        <div className="rule-condition__head">
          <span className="rule-condition__lead" data-datum={parity.lead} data-kind="text">
            {lead}
          </span>
          <div className="rule-condition__seg">
            <button
              type="button"
              data-on={tree.operator === 'and' ? '1' : undefined}
              disabled={readOnly}
              data-datum={parity.all}
              data-kind="button"
              data-testid={`${testId}-all`}
              onClick={() => set({ operator: 'and' })}
            >
              {t(`${KEY}.all`)}
            </button>
            <button
              type="button"
              data-on={tree.operator === 'or' ? '1' : undefined}
              disabled={readOnly}
              data-datum={parity.any}
              data-kind="button"
              data-testid={`${testId}-any`}
              onClick={() => set({ operator: 'or' })}
            >
              {t(`${KEY}.any`)}
            </button>
          </div>
          <span
            className="rule-condition__head-note"
            data-datum={parity.ofTheFollowing}
            data-kind="text"
          >
            {t(`${KEY}.ofTheFollowing`)}
          </span>
          <span className="rule-condition__spacer" data-datum={parity.spacer} data-kind="text" />
          {!readOnly && tree.expressions.length > 0 && (
            <button
              type="button"
              className="rule-condition__clear"
              data-datum={parity.clear}
              data-kind="button"
              data-testid={`${testId}-clear`}
              onClick={() => set({ expressions: [] })}
            >
              {t(`${KEY}.clear`)}
            </button>
          )}
        </div>
        <div className="rule-condition__body">
          {tree.expressions.map((entry, index) =>
            isCondition(entry) ? (
              <div className="rule-condition__row" key={`group-${index}`} data-container="each(expressions)">
                <span
                  className="rule-condition__joiner"
                  data-datum={parity.group.joiner}
                  data-kind="text"
                >
                  {index === 0 ? '' : joinerWord}
                </span>
                <div className="rule-condition__group">
                  <div className="rule-condition__group-head">
                    <span data-datum={parity.group.label} data-kind="text">
                      {t(`${KEY}.group`)}
                    </span>
                    <div className="rule-condition__seg">
                      <button
                        type="button"
                        data-on={entry.operator === 'and' ? '1' : undefined}
                        disabled={readOnly}
                        data-datum={parity.group.all}
                        data-kind="button"
                        onClick={() => setAt(index, { operator: 'and' })}
                      >
                        {t(`${KEY}.all`)}
                      </button>
                      <button
                        type="button"
                        data-on={entry.operator === 'or' ? '1' : undefined}
                        disabled={readOnly}
                        data-datum={parity.group.any}
                        data-kind="button"
                        onClick={() => setAt(index, { operator: 'or' })}
                      >
                        {t(`${KEY}.any`)}
                      </button>
                    </div>
                    <span
                      className="rule-condition__spacer"
                      data-datum={parity.group.spacer}
                      data-kind="text"
                    />
                    {!readOnly && (
                      <button
                        type="button"
                        className="rule-condition__x"
                        aria-label={t(`${KEY}.removeGroup`)}
                        data-datum={parity.group.remove}
                        data-kind="button"
                        onClick={() => removeAt(index)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {(entry.expressions ?? []).map((sub, k) =>
                    isCondition(sub)
                      ? null
                      : expressionRow(
                          sub,
                          `${index}.${k}`,
                          k,
                          t(`${KEY}.joiner.${entry.operator}`),
                          parity.groupExpression,
                          (patch) =>
                            setAt(index, {
                              expressions: entry.expressions.map((x, y) =>
                                y === k ? { ...x, ...patch } : x
                              ),
                            } as Partial<BusinessRuleCondition>),
                          () =>
                            setAt(index, {
                              expressions: entry.expressions.filter((_, y) => y !== k),
                            } as Partial<BusinessRuleCondition>)
                        )
                  )}
                  {!readOnly && (
                    <div className="rule-condition__addrow">
                      <button
                        type="button"
                        className="rule-condition__addpill"
                        data-datum={parity.group.add}
                        data-kind="button"
                        onClick={() =>
                          setAt(index, {
                            expressions: [...(entry.expressions ?? []), blank()],
                          } as Partial<BusinessRuleCondition>)
                        }
                      >
                        {t(`${KEY}.add`)}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              expressionRow(
                entry,
                String(index),
                index,
                joinerWord,
                parity.expression,
                (patch) => setAt(index, patch),
                () => removeAt(index)
              )
            )
          )}
          {!tree.expressions.length && <div className="rule-condition__empty">{emptyNote}</div>}
          {!readOnly && (
            <div className="rule-condition__addrow">
              <button
                type="button"
                className="rule-condition__addpill"
                data-datum={parity.addCondition}
                data-kind="button"
                data-testid={`${testId}-add-condition`}
                onClick={() => set({ expressions: [...tree.expressions, blank()] })}
              >
                {t(`${KEY}.addCondition`)}
              </button>
              <button
                type="button"
                className="rule-condition__addlink"
                data-datum={parity.addGroup}
                data-kind="button"
                data-testid={`${testId}-add-group`}
                onClick={() =>
                  set({
                    expressions: [
                      ...tree.expressions,
                      { operator: 'or', expressions: [blank()] },
                    ],
                  })
                }
              >
                {t(`${KEY}.addGroup`)}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

RuleConditionBuilderMolecule.displayName = 'RuleConditionBuilderMolecule';

export default RuleConditionBuilderMolecule;
